import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import OverrideIndicator from 'lumen/components/hotel/pricing-assistant/override-indicator';
import type { Period } from 'lumen/models';
import { RESTRICTION_MODEL_CONTEXTS } from 'lumen/utils/constants/channel-pricing-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  formatMinLosRecommendation,
  formatMinLosValue,
} from 'lumen/utils/pricing-assistant/restriction-display-utils';

import styles from './pricing-assistant-table-tooltip.module.css';

interface HotelPricingAssistantTableTooltipsTooltipStayRestrictionsTableSignature {
  Args: {
    period: Period;
  };
}

export default class HotelPricingAssistantTableTooltipsTooltipStayRestrictionsTable extends Component<HotelPricingAssistantTableTooltipsTooltipStayRestrictionsTableSignature> {
  @service declare featureToggle: Services['feature-toggle'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  get failedRestrictionCount(): number {
    return (
      (this.args.period.hasFailedHotelMinLos ? 1 : 0) +
      (this.args.period.hasFailedDeepDiscountMinLos ? 1 : 0)
    );
  }

  get formattedDeepDiscountMinLosRecommendation(): string {
    return formatMinLosRecommendation({
      displayedValue: this.args.period.effectiveDeepDiscountMinLosWithOverride,
      intl: this.intl,
      recommendedValue:
        this.args.period.deepDiscountRestrictionRecommendation?.minLosValue,
    });
  }

  get formattedDeepDiscountMinLosValue(): string {
    return formatMinLosValue({
      intl: this.intl,
      value: this.args.period.effectiveDeepDiscountMinLosWithOverride,
    });
  }

  get formattedFailedDeepDiscountMinLosValue(): string {
    return formatMinLosValue({
      intl: this.intl,
      value: this.args.period.failedDeepDiscountMinLosValue,
    });
  }

  get formattedFailedHotelMinLosValue(): string {
    return formatMinLosValue({
      intl: this.intl,
      value: this.args.period.failedHotelMinLosValue,
    });
  }

  get formattedHotelMinLosRecommendation(): string {
    return formatMinLosRecommendation({
      displayedValue: this.args.period.effectiveHotelMinLosWithOverride,
      intl: this.intl,
      recommendedValue:
        this.args.period.hotelLevelRestrictionRecommendation?.minLosValue,
    });
  }

  get formattedHotelMinLosValue(): string {
    return formatMinLosValue({
      intl: this.intl,
      value: this.args.period.effectiveHotelMinLosWithOverride,
    });
  }

  get hasDeepDiscountMinLosOverride(): boolean {
    return this.args.period.hasDeepDiscountMinLosOverride;
  }

  get hasHotelMinLosOverride(): boolean {
    return this.args.period.hasHotelMinLosOverride;
  }

  /* The BE resolves precedence between the hotel and rate-group MinLOS. When the applied
  context is HOTEL_LEVEL, the Deep Discount rate group's own value is not what gets pushed. */
  get hotelRestrictionApplies(): boolean {
    return (
      this.args.period.deepDiscountMinLosAppliedContext ===
      RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL
    );
  }

  get isDeepDiscountMinLosLoading(): boolean {
    return this.args.period.hasDeepDiscountMinLosActionInProgress;
  }

  get isHotelMinLosLoading(): boolean {
    return this.args.period.hasHotelMinLosActionInProgress;
  }

  get shouldShowDeepDiscountRecommendedRow(): boolean {
    return this.showDeepDiscountRow && this.hasDeepDiscountMinLosOverride;
  }

  get shouldShowHotelRecommendedRow(): boolean {
    return this.hasHotelMinLosOverride;
  }

  get showDeepDiscountRow(): boolean {
    const metaInfo =
      this.pricingAssistantDataRequestService
        .requestPricingAssistantSubscriptionMetaTask.lastSuccessful?.value;

    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_DEEP_DISCOUNT,
      ) &&
      (metaInfo?.isDeepDiscountRateGroupConfigured ?? false)
    );
  }

  get showFailedRestriction(): boolean {
    return this.args.period.shouldShowStayRestrictionException;
  }

  <template>
    <table
      class="default-table {{styles.table}}"
      data-test-stay-restrictions-section
    >
      <thead>
        <tr>
          <th class="{{styles.table-header}} text-200--bold">
            {{t "pricing_assistant.stay_restrictions.header"}}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="{{styles.table-row-cell}} text-200--regular">
            {{#if this.isHotelMinLosLoading}}
              <div
                class="loading-pulse w-5/6"
                data-test-id="stay-restrictions-hotel-loading"
              ></div>
              <div class="loading-pulse w-1/6"></div>
            {{else}}
              <p class="text-200--regular">
                {{t "general.hotel"}}
              </p>
              <div class={{styles.value-row}}>
                <p
                  class="text-200--regular"
                  data-test-stay-restrictions-hotel-value
                >
                  {{this.formattedHotelMinLosValue}}
                </p>
                {{#if this.hasHotelMinLosOverride}}
                  <span class={{styles.override-indicator}}>
                    <OverrideIndicator @variant="automation-off" />
                  </span>
                {{/if}}
              </div>
            {{/if}}
          </td>
        </tr>
        {{! Row for recommended MinLOS when override is active }}
        {{#if this.shouldShowHotelRecommendedRow}}
          <tr>
            <td
              class="{{styles.table-row-cell}}
                {{styles.indented-row}}
                text-200--regular"
            >
              <p class="text-200--regular">
                {{t "pricing_assistant.stay_restrictions.recommended"}}
              </p>
              {{#if this.isHotelMinLosLoading}}
                <div class="loading-pulse w-1/6"></div>
              {{else}}
                <div class={{styles.value-row}}>
                  <p
                    class="text-200--regular"
                    data-test-id="stay-restrictions-hotel-recommended-value"
                  >
                    {{this.formattedHotelMinLosRecommendation}}
                  </p>
                </div>
              {{/if}}
            </td>
          </tr>
        {{/if}}
        {{! Deep Discount rate group row }}
        {{#if this.showDeepDiscountRow}}
          <tr>
            <td class="{{styles.table-row-cell}} text-200--regular">
              {{#if this.isDeepDiscountMinLosLoading}}
                <div
                  class="loading-pulse w-5/6"
                  data-test-id="stay-restrictions-deep-discount-loading"
                ></div>
                <div class="loading-pulse w-1/6"></div>
              {{else}}
                <p class="text-200--regular">
                  {{t "pricing_assistant.stay_restrictions.deep_discount"}}
                </p>
                <div class="flex flex-col items-end">
                  <div class={{styles.value-row}}>
                    <p
                      class="text-200--regular
                        {{if
                          this.hotelRestrictionApplies
                          'strikethrough-text'
                        }}"
                      data-test-stay-restrictions-deep-discount-value
                    >
                      {{this.formattedDeepDiscountMinLosValue}}
                    </p>
                    {{#if this.hasDeepDiscountMinLosOverride}}
                      <span class={{styles.override-indicator}}>
                        <OverrideIndicator @variant="automation-off" />
                      </span>
                    {{/if}}
                  </div>
                  {{#if this.hotelRestrictionApplies}}
                    <p class={{styles.comparison-line}}>
                      {{t
                        "pricing_assistant.stay_restrictions.hotel_restriction_applies"
                      }}
                    </p>
                  {{/if}}
                </div>
              {{/if}}
            </td>
          </tr>
        {{/if}}
        {{! Row for the recommended Deep Discount MinLOS when its override is active }}
        {{#if this.shouldShowDeepDiscountRecommendedRow}}
          <tr>
            <td
              class="{{styles.table-row-cell}}
                {{styles.indented-row}}
                text-200--regular"
            >
              <p class="text-200--regular">
                {{t "pricing_assistant.stay_restrictions.recommended"}}
              </p>
              {{#if this.isDeepDiscountMinLosLoading}}
                <div class="loading-pulse w-1/6"></div>
              {{else}}
                <div class={{styles.value-row}}>
                  <p
                    class="text-200--regular"
                    data-test-id="stay-restrictions-deep-discount-recommended-value"
                  >
                    {{this.formattedDeepDiscountMinLosRecommendation}}
                  </p>
                </div>
              {{/if}}
            </td>
          </tr>
        {{/if}}
        {{! Failed restriction(s) row when a MinLOS push has failed }}
        {{#if this.showFailedRestriction}}
          <tr>
            <td
              class="{{styles.table-row-cell}} text-200--regular"
              data-test-failed-restriction-row
            >
              <p class="text-200--regular">
                {{t
                  "pricing_manager.failed_restriction"
                  count=this.failedRestrictionCount
                }}
              </p>
              <div class="flex flex-col items-end">
                {{#if @period.hasFailedHotelMinLos}}
                  <p class="text-200--regular">
                    {{t "general.hotel"}}:
                    <span data-test-failed-restriction-hotel-value>
                      {{this.formattedFailedHotelMinLosValue}}
                    </span>
                  </p>
                {{/if}}
                {{#if @period.hasFailedDeepDiscountMinLos}}
                  <p class="text-200--regular">
                    {{t "pricing_assistant.stay_restrictions.deep_discount"}}:
                    <span data-test-failed-restriction-deep-discount-value>
                      {{this.formattedFailedDeepDiscountMinLosValue}}
                    </span>
                  </p>
                {{/if}}
              </div>
            </td>
          </tr>
        {{/if}}
      </tbody>
    </table>
  </template>
}
